import { useState, useCallback } from 'react';
import type { Transaction, TransactionSummary, MonthlyStats, SelectedRegion } from '../types';
import { fetchTransactions, calculateSummary, calculateMonthlyStats } from '../lib/api';
import RegionSelector from '../components/RegionSelector';
import DataTable, { TransactionTable } from '../components/DataTable';
import ChartView from '../components/ChartView';
import ExportButton from '../components/ExportButton';

export default function HomePage() {
  const [selectedRegion, setSelectedRegion] = useState<SelectedRegion | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [summaries, setSummaries] = useState<TransactionSummary[]>([]);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);
  const [selectedApt, setSelectedApt] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // 지역 선택 시 데이터 로드
  const handleRegionSelect = useCallback(async (region: SelectedRegion) => {
    setSelectedRegion(region);
    setLoading(true);
    setError(undefined);
    setSelectedApt(undefined);

    try {
      // 실제 앱에서는 API 호출
      const data = await fetchTransactions(region.dong);
      setTransactions(data);
      setSummaries(calculateSummary(data));
      setMonthlyStats(calculateMonthlyStats(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : '데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 선택한 아파트의 거래 내역
  const selectedAptTransactions = selectedApt
    ? transactions.filter(t => t.aptName === selectedApt)
    : transactions;

  return (
    <div className="space-y-6">
      {/* 지역 선택 */}
      <RegionSelector 
        onRegionSelect={handleRegionSelect}
        initialRegion={selectedRegion || undefined}
      />

      {/* 에러 표시 */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          ⚠️ {error}
        </div>
      )}

      {/* 로딩 */}
      {loading && (
        <div className="flex justify-center py-8">
          <div className="loading"></div>
        </div>
      )}

      {/* 데이터 표시 */}
      {!loading && selectedRegion && transactions.length > 0 && (
        <>
          {/* 지역 정보 */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-2">
              📍 {selectedRegion.sidoName} {selectedRegion.sigunguName} {selectedRegion.dongName}
            </h2>
            <p className="text-gray-600">
              총 {transactions.length}건의 실거래가 데이터, {summaries.length}개 아파트 단지
            </p>
          </div>

          {/* 내보내기 버튼 */}
          <div className="flex justify-end">
            <ExportButton
              transactions={transactions}
              summaries={summaries}
              regionName={`${selectedRegion.sidoName} ${selectedRegion.sigunguName} ${selectedRegion.dongName}`}
            />
          </div>

          {/* 아파트별 시세 요약 테이블 */}
          <DataTable
            summaries={summaries}
            onRowClick={setSelectedApt}
            selectedApt={selectedApt}
          />

          {/* 차트 */}
          <ChartView
            monthlyStats={monthlyStats}
            summaries={summaries}
          />

          {/* 상세 거래 내역 */}
          <TransactionTable transactions={selectedAptTransactions} />

          {/* 분석 레포트 링크 */}
          <div className="card bg-primary-50">
            <h3 className="font-semibold mb-2">📊 분석 레포트 생성</h3>
            <p className="text-gray-600 mb-3">
              선택한 지역의 데이터를 바탕으로 상세 분석 레포트를 생성합니다.
            </p>
            <button 
              className="btn btn-primary"
              onClick={() => {
                // 레포트 페이지로 이동 (향후 구현)
                alert('분석 레포트 기능은 준비 중입니다.');
              }}
            >
              레포트 생성하기
            </button>
          </div>
        </>
      )}

      {/* 데이터 없음 */}
      {!loading && selectedRegion && transactions.length === 0 && (
        <div className="card text-center py-8">
          <p className="text-gray-500">
            선택한 지역의 실거래가 데이터가 없습니다.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            다른 지역을 선택해보세요.
          </p>
        </div>
      )}

      {/* 초기 상태 */}
      {!selectedRegion && (
        <div className="card text-center py-12">
          <div className="text-6xl mb-4">🏢</div>
          <h2 className="text-xl font-semibold mb-2">아파트 가격 동향 분석</h2>
          <p className="text-gray-600 mb-4">
            지역을 선택하여 실거래가 데이터를 조회하세요.
          </p>
          <p className="text-sm text-gray-400">
            상단의 드롭다운에서 시/도 → 시/군/구 → 읍/면/동 순으로 선택할 수 있습니다.
          </p>
        </div>
      )}
    </div>
  );
}